import type { ColumnIndex } from '../interfaces/ColumnRange';
import type { ItemsLayout } from '../interfaces/ItemsLayout';
import type { LayoutInterval } from '../interfaces/LayoutInterval';
import { minmax } from './minmax';

/**
 * This class provides the standard linear transformation between
 * layout offsets and relative vertical positions, used by React elements.
 */
export class LinearItemsLayout implements ItemsLayout {
    public constructor(
        /**
         * The calendar surface store or object with similar properties.
         * In particular, dateRange should contain the start of day of the first and last days in the view.
         */
        private store: {
            dayLength: number;
            userOptions: {
                hourIncrement?: number;
            };
        },
        /** Minimum size of the items to be displayed in the calendar surface, in corresponding minutes. */
        private minimumItemSizeInMinutes: number = 15
    ) {}

    /**
     * Gets the relative vertical positions of the given layout interval.
     * @param interval A layout interval, expected to have been shrinked to the interval of a particular column.
     * @returns The relative distance the interval has from the top of the view and its relative height (both in %).
     */
    public getVerticalLayout(interval: LayoutInterval) {
        const columnLength = this.store.dayLength;

        // Start and Duration, in "offset" units
        const start = interval.start % columnLength;
        const duration = Math.max(interval.end - interval.start, this.minimumItemSizeInMinutes);

        // Top and Height, in % units
        const top = Math.max(0, start / columnLength);
        const height = Math.min(duration / columnLength, 1 - top);

        return { top, height };
    }

    /**
     * Converts a vertical point in a column to the smallest layout interval snapped in that position.
     * @param columnIndex Index of the column in which the point is being calculated.
     * @param pageYPosition The vertical position of the point, in page coordinates. Ex: a mouseDown pageY parameter.
     * @param boundaries The top and height values of the bounding rectangle of the target element clicked. Ex: divElement.getBoundingClientRect()
     */
    public pointToInterval(
        columnIndex: ColumnIndex,
        pageYPosition: number,
        boundaries: { top: number; height: number }
    ): LayoutInterval {
        const columnLength = this.store.dayLength;

        const positionInPixels = minmax(
            boundaries.top,
            pageYPosition,
            boundaries.top + boundaries.height - 1
        );

        const offsetInPixels = positionInPixels - boundaries.top;
        const minutesPerPixel = columnLength / boundaries.height;
        const offsetInMinutes = offsetInPixels * minutesPerPixel;

        const hourIncrement = this.store.userOptions.hourIncrement;
        const snapInMinutes = hourIncrement == void 0 ? 30 : hourIncrement;
        const startOffset = Math.floor(offsetInMinutes / snapInMinutes) * snapInMinutes;
        const endOffset = startOffset + snapInMinutes;

        const columnOffset = columnIndex * columnLength;
        const start = columnOffset + startOffset;
        const end = columnOffset + endOffset;

        return { start, end };
    }
}
